// Step de Dados da Empresa do onboarding
import { useState } from 'react';
import { Input } from '../common/Input';
import { Button } from '../common/Button';
import { AlertCircle } from 'lucide-react';
import { formatCNPJ, formatPhone } from '../../utils/formatters';
import { validateCNPJ, validateEmail } from '../../utils/validators';

interface CompanyDataStepProps {
  data: {
    razaoSocial: string;
    nomeFantasia: string;
    cnpj: string;
    inscricaoEstadual: string;
    email: string;
    telefone: string;
  };
  onChange: (data: Partial<CompanyDataStepProps['data']>) => void;
  onNext: () => void;
  onBack: () => void;
}

export function CompanyDataStep({ data, onChange, onNext, onBack }: CompanyDataStepProps) {
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!data.razaoSocial.trim()) {
      newErrors.razaoSocial = 'Razão Social é obrigatória';
    }

    if (!data.nomeFantasia.trim()) {
      newErrors.nomeFantasia = 'Nome Fantasia é obrigatório';
    }

    if (!data.cnpj.trim()) {
      newErrors.cnpj = 'CNPJ é obrigatório';
    } else if (!validateCNPJ(data.cnpj)) {
      newErrors.cnpj = 'CNPJ inválido';
    }

    if (!data.email.trim()) {
      newErrors.email = 'Email é obrigatório';
    } else if (!validateEmail(data.email)) {
      newErrors.email = 'Email inválido';
    }

    if (!data.telefone.trim()) {
      newErrors.telefone = 'Telefone é obrigatório';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validate()) {
      onNext();
    }
  };

  const handleCNPJChange = (value: string) => {
    const formatted = formatCNPJ(value);
    onChange({ cnpj: formatted });
  };

  const handlePhoneChange = (value: string) => {
    const formatted = formatPhone(value);
    onChange({ telefone: formatted });
  };

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
          Dados da Empresa
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Preencha as informações básicas da sua empresa
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Razão Social"
          value={data.razaoSocial}
          onChange={(e) => onChange({ razaoSocial: e.target.value })}
          error={errors.razaoSocial}
          required
          placeholder="Ex: Empresa LTDA"
        />

        <Input
          label="Nome Fantasia"
          value={data.nomeFantasia}
          onChange={(e) => onChange({ nomeFantasia: e.target.value })}
          error={errors.nomeFantasia}
          required
          placeholder="Ex: Minha Empresa"
        />

        <Input
          label="CNPJ"
          value={data.cnpj}
          onChange={(e) => handleCNPJChange(e.target.value)}
          error={errors.cnpj}
          required
          placeholder="00.000.000/0000-00"
          maxLength={18}
        />

        <Input
          label="Inscrição Estadual"
          value={data.inscricaoEstadual}
          onChange={(e) => onChange({ inscricaoEstadual: e.target.value })}
          placeholder="123456789"
        />

        <Input
          label="Email"
          type="email"
          value={data.email}
          onChange={(e) => onChange({ email: e.target.value })}
          error={errors.email}
          required
          placeholder="contato@empresa.com"
        />

        <Input
          label="Telefone"
          value={data.telefone}
          onChange={(e) => handlePhoneChange(e.target.value)}
          error={errors.telefone}
          required
          placeholder="(11) 99999-9999"
          maxLength={15}
        />
      </div>

      {Object.keys(errors).length > 0 && (
        <div className="p-4 bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded-xl flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-error-600 dark:text-error-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-error-700 dark:text-error-300">
            <p className="font-semibold mb-1">Por favor, corrija os seguintes erros:</p>
            <ul className="list-disc list-inside space-y-1">
              {Object.values(errors).map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div className="flex justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
        <Button variant="secondary" onClick={onBack}>
          Voltar
        </Button>
        <Button variant="primary" onClick={handleNext}>
          Próximo
        </Button>
      </div>
    </div>
  );
}
